import * as React from 'react'

import { useBootstrapApp } from '@src/hooks/useBootstrapApp'

import { getSite, getSiteConfig } from '@src/state/site/selectors'
import { SiteState } from '@src/state/site/types'
import { freeTrialTCMatch, workspaceMatch } from '@src/utils/routes'
import { parse, ParsedQs, stringify } from 'qs'
import { useSelector } from 'react-redux'
import { useLocation, useRouteMatch } from 'react-router'

export const CWC_ROLLOUT_FEATURE_KEY = 'chegg-writing-center-rollout-2'

export const fromWfe = { allocationFrom: 'wfe' } as const

export const useCwcRedirect = ({
  uploadId,
  shouldRedirect,
  parsedSearch,
  assignLocation,
  cheggEnvUrl,
}: {
  uploadId: string
  shouldRedirect: boolean
  parsedSearch: ParsedQs
  assignLocation: typeof window.location.assign
  cheggEnvUrl: string
}): void => {
  React.useEffect(() => {
    if (!shouldRedirect) {
      return
    }

    const { runChecks, filename, token, ...incomingSearch } = parsedSearch

    // If run checks is NOT present in the parsed query string,
    // this is not a new upload. Redirect to cwc home page.
    if (runChecks !== 'true') {
      assignLocation(
        `${cheggEnvUrl}tools/writing-center/${stringify(
          { ...incomingSearch, ...fromWfe },
          { addQueryPrefix: true }
        )}`
      )
      return
    }

    const outgoingSearch = stringify(
      {
        ...incomingSearch,
        ...fromWfe,
        uploadId,
        filename,
      },
      { addQueryPrefix: true }
    )

    const href = `${cheggEnvUrl}tools/writing-center/draft/${outgoingSearch}`

    assignLocation(href)
  }, [shouldRedirect, uploadId, parsedSearch, cheggEnvUrl])
}

/**
 * We should not direct any user away if they are not in a chegg brand site,
 * or if they are viewing terms and conditions.
 * Otherwise, users where the feature is enabled are redirected into Chegg
 * Writing Center (chegg-web).
 */
export const getShouldRedirect = ({
  termsAndConditionsMatch,
  site,
}: {
  termsAndConditionsMatch: ReturnType<typeof useRouteMatch>
  site: SiteState
}): boolean => {
  const isChegg = site === 'chegg'

  return !termsAndConditionsMatch && isChegg
}

export const useCwcRollout = () => {
  const termsAndConditionsMatch = useRouteMatch(freeTrialTCMatch)
  const workspacePathMatch = useRouteMatch<{ id: string }>(workspaceMatch)
  const uploadId = workspacePathMatch?.params?.id ?? ''
  const { cheggEnvUrl } = useSelector(getSiteConfig)
  const { search } = useLocation()
  const site = useSelector(getSite)
  const [parsedSearch, setParsedSearch] = React.useState<ParsedQs>({})

  React.useEffect(() => {
    // WFE manipulates the query strings as the app bootstraps.
    // This method keeps all values in memory to pass them along on redirect.
    // Useful for any paid search, campaigns we may not know about.
    setParsedSearch(parsedSearch => {
      return {
        ...parse(search, { ignoreQueryPrefix: true }),
        ...parsedSearch,
      }
    })
  }, [search])

  const shouldRedirect = getShouldRedirect({
    site,
    termsAndConditionsMatch,
  })

  useBootstrapApp(shouldRedirect)

  useCwcRedirect({
    cheggEnvUrl,
    uploadId,
    parsedSearch,
    shouldRedirect,
    // Passing `location.assign` in for ease of testing.
    // Need to bind the function `this` context.
    assignLocation: window.location.assign.bind(window.location),
  })
}

const CwcRollout: React.FC = ({ children }) => {
  useCwcRollout()
  return <>{children}</>
}

export default CwcRollout



// WEBPACK FOOTER //
// ./src/components/Router/CwcRollout.tsx